define('presentation/signup/viewModels/signupPaymentMethodViewModel',[
        'businessServices/country/countryCode',
        'businessServices/country/countryRegions',
        'businessServices/converters/textConverter',
        'businessServices/creditCards/creditCardTypes',
        'businessServices/signup/signupSessionStore',
        'common/calendar/monthList',
        'common/collections/enumerationKeyValueConverter',
        'common/converters/phoneNumberFormatter',
        'common/encryption/crypto',
        'common/promises/promiseFactory',
        'common/storage/workSessionIdStorage',
        'common/time/date',
        'constants/billingMethodConstants',
        'constants/countryEnumerations',
        'constants/creditCardExpirationYearConstants',
        'entities/paymentMethodEntity',
        'presentation/common/window/windowControl',
        'presentation/signup/facades/signupPaymentMethodFacade',
        'presentation/signup/validators/signupPaymentMethodValidator',
        'settings/navigationConfiguration',
        'presentation/signup/common/signupNavigationManager',
        'constants/signupSessionFlowConstants',
        'constants/signupSessionStatusConstants'
    ],
    function() {
        return function() {
            const self = this;

            const PromiseFactoryConstructor = require('common/promises/promiseFactory');
            const _promiseFactory = new PromiseFactoryConstructor();

            const TextConverterConstructor = require('businessServices/converters/textConverter');
            const _textConverter = new TextConverterConstructor();

            const FacadeConstructor = require('presentation/signup/facades/signupPaymentMethodFacade');
            const ValidatorConstructor = require('presentation/signup/validators/signupPaymentMethodValidator');

            const CreditCardTypesConstructor = require('businessServices/creditCards/creditCardTypes');
            const CountryCodeConstructor = require('businessServices/country/countryCode');
            const CountryRegionsConstructor = require('businessServices/country/countryRegions');
            const DateConstructor = require('common/time/date');
            const MonthConstructor = require('common/calendar/monthList');
            const _months = new MonthConstructor().monthList;

            const PhoneNumberFormatterConstructor = require('common/converters/phoneNumberFormatter');
            const _phoneNumberFormatter = new PhoneNumberFormatterConstructor();

            const _countries = new CountryCodeConstructor();
            const _countryRegions = new CountryRegionsConstructor();
            const _creditCardExpirationYear = require('constants/creditCardExpirationYearConstants');
            const _creditCardTypes = new CreditCardTypesConstructor();

            const _billingMethodConstants = require('constants/billingMethodConstants');
            const _countryEnumerations = require('constants/countryEnumerations');
            const _i18n = require('i18next');
            const _keyValueConverter = require('common/collections/enumerationKeyValueConverter');
            const _navigationConfiguration = require('settings/navigationConfiguration');
            const _signupSessionFlowConstants = require('constants/signupSessionFlowConstants');
            const _signupSessionStatusConstants = require('constants/signupSessionStatusConstants');
            const _workSessionIdStorage = require('common/storage/workSessionIdStorage');

            let _facade = null;
            let _validator = null;
            let _disposables = [];
            let _signupNavigationManager = null;
            let _signupSessionStore = null;
            let _startTrialClicked = false;
            let _modalOtherNavigationRouteId = null;

            const MODAL_OTHER_AUTHORIZED_SIGNATURE = {
                text: 'missingAuthorizedSignature',
                buttonText: 'modalAuthorizedSignatureButtonText',
                routeId: 'signupPhoneNumberPortAuthorization',
            };
            const MODAL_OTHER_PASSWORD = {
                text: 'missingPassword',
                buttonText: 'modalPasswordButtonText',
                routeId: 'signupSecurity',
            };

            const _showModalOther = (variant) => {
                self.modalOtherText(variant.text);
                self.modalOtherButtonText(variant.buttonText);
                _modalOtherNavigationRouteId = _navigationConfiguration.routesById[variant.routeId].routeId;
                self.isModalOtherVisible(true);
            };
            let _reservationFailedRouteId = null;

            const _isSignupForCustomer = _workSessionIdStorage.getWorkSessionId() !== null && _workSessionIdStorage.getWorkSessionId() !== undefined;

            const _prepareStatesProvinces = (statesProvinces) => {
                return statesProvinces.map((state) => {
                    state.id = state.abbreviation;
                    state.text = state.name;
                    return state;
                });
            };

            const _onCountryChange = (countryCode) => {
                self.postalCode("");
                self.statesProvinces(_prepareStatesProvinces(_countryRegions.findByAbbreviation(countryCode[0]).statesProvinces));
                self.stateOrProvince([self.statesProvinces()[0].id]);
            };

            const _currentCountry = (country = null) => {
                if (country){
                    const currentCountry = _countries.findByAbbreviation(country);
                    return self.countries.find(item => item.text === currentCountry.text);
                } else {
                    const currentCountry = _countries.getDefaultCountry();
                    return self.countries.find(item => item.text === currentCountry.text);
                }
            };

            const _currentMonth = (month = null) => {
                if (month){
                    return _months.find(item => item.value === month);
                } else {
                    const currentMonth = new DateConstructor();
                    currentMonth.setToday();
                    return _months.find(item => item.value === currentMonth.monthPadded());
                }
            };

            const _currentYear = (year = null) => {
                if (year){
                    return self.expirationYears.find(item => item.text === year);
                } else {
                    const currentYear = new Date().getFullYear();
                    return self.expirationYears.find(item => item.text === currentYear);
                }
            };

            const _populateStateDropdown = () => {
                self.statesProvinces =  ko.observableArray(_prepareStatesProvinces(_countryRegions.findByAbbreviation(_countryEnumerations.usa).statesProvinces));
            };

            const _populateCountryDropdown = () => {
                self.countries =  _countries.countryCodes.map((country) => {
                    country.id = country.countryAbbreviation;
                    country.text = country.name;
                    return country;
                });
            };

            const _populateBillingMethodDropdown = () => {
                self.billingMethods = _keyValueConverter
                    .convertToKeyValues(_billingMethodConstants)
                    .map((billingMethod) => {
                        const i18nKey = _textConverter.camelCase(billingMethod.key);
                        return {
                            id: billingMethod.value.toString(),
                            text: _i18n.t(`signupPaymentMethod:${i18nKey}`)
                        };
                    });
            };

            const _populateExpirationDateDropdowns = () => {
                self.expirationMonths = _months.map((month) => {
                    month.id = month.value;
                    month.text = month.value;
                    return month;
                });

                const currentYear = new Date().getFullYear();
                self.expirationYears = Array.from(
                    Array(_creditCardExpirationYear.yearsFromCurrent),
                    (_, i) => ({id: currentYear + i, text: currentYear + i})
                );
            };

            const _createFreeTrialEndDate = () => {
                const trialEnd = new DateConstructor();
                trialEnd.setToday();
                trialEnd.addDays(14);
                return `${trialEnd.monthName()} ${trialEnd.dayRank()}`;
            };

            const _onBillingMethodChange = () => {
                if (self.isCardEntryVisible() === false) {
                    self.cardHolderName("");
                    self.cardNumber("");
                    self.cvvCode("");
                    self.isBypassPreAuthorizationSelected(false);
                } else {
                    self.cardHolderName.validate(true);
                    self.cardNumber.validate(true);
                    self.cvvCode.validate(true);
                    self.expirationDate.validate(true);
                }

                if (self.selectedBillingMethod()[0]) {
                    _facade.saveBillingMethod(self.selectedBillingMethod()[0]);
                }
            };

            const _saveCardHolderName = (newValue, oldValue) => {
                if (newValue !== oldValue && self.cardHolderName.isValid()) {
                    _facade.saveCardHolderName(self.cardHolderName());
                }
            };
            const _saveExpirationDate = () => {
                if (self.expirationDate.isValid()) {
                    _facade.saveExpirationDate(self.expirationMonth()[0], self.expirationYear()[0]);
                }
            };
            const _saveAddressLineOne = () => {
                if (self.addressLineOne.isValid()) {
                    _facade.saveAddressLineOne(self.addressLineOne());
                }
            };
            const _saveAddressLineTwo = () => {
                if (self.addressLineTwo()) {
                    _facade.saveAddressLineTwo(self.addressLineTwo());
                }
            };
            const _saveCity = () => {
                if (self.city.isValid()) {
                    _facade.saveCity(self.city());
                }
            };
            const _saveStateOrProvince = () => {
                if (self.stateOrProvince()[0] && self.country()[0]) {
                    _facade.saveStateOrProvince(self.country()[0], self.stateOrProvince()[0]);
                }
            };
            const _savePostalCode = (newValue, oldValue) => {
                if (newValue !== oldValue && self.postalCode.isValid() && self.country()) {
                    _facade.savePostalCode(self.country()[0], self.postalCode());
                    if (self.signupResult() === "invalid_postal_code") {
                        self.signupResult("");
                    }
                }
            };

            self.startTrial = () => _promiseFactory.deferIndefinitely(deferredObject => {
                if (_startTrialClicked) {
                    deferredObject.resolve(true);
                    return;
                }

                _startTrialClicked = true;
                self.signupResult("");
                _validator.validate()
                    .fail(deferredObject.reject)
                    .done(isValid => {
                        self.showValidation(true);

                        if (!isValid) {
                            deferredObject.resolve(true);
                            _startTrialClicked = false;
                            return;
                        }

                        const date = new DateConstructor();

                        const isCardEntry = self.isCardEntryVisible();

                        const paymentMethod = {
                            billingMethod: self.selectedBillingMethod()[0],
                            cardHolderName: isCardEntry ? self.cardHolderName() : "",
                            cardType: isCardEntry ? _creditCardTypes.getCreditCardType(self.cardNumber()) : "",
                            countryCode: self.country()[0],
                            cardNumber: isCardEntry ? self.cardNumber() : "",
                            cvvCode: isCardEntry ? self.cvvCode() : "",
                            expirationDate: isCardEntry ? date.getExpirationDate(self.expirationYear()[0], self.expirationMonth()[0]) : null,
                            postalCode: self.postalCode(),
                            addressLineOne: self.addressLineOne(),
                            addressLineTwo: self.addressLineTwo(),
                            city: self.city(),
                            stateProvince: self.stateOrProvince()[0],
                            bypassPreAuthorization: isCardEntry && self.isBypassPreAuthorizationSelected(),
                            bypassWelcomeEmail: self.isBypassWelcomeEmailSelected()
                        };

                        const _isFraudulentPaymentInformation = () => {
                            if (self.isCardEntryVisible() === false) {
                                return _promiseFactory.defer((promise) => promise.resolve(false));
                            }
                            return _facade.isFraudulentPaymentInformation(paymentMethod);
                        };

                        _isFraudulentPaymentInformation()
                            .fail(deferredObject.reject)
                            .done((isFraudulent) => {

                                if (isFraudulent) {
                                    _signupNavigationManager.navigateToErrorPage();
                                    deferredObject.resolve({keepSpinning: false});
                                    return;
                                }

                                _facade.createAccount(paymentMethod)
                                    .fail(deferredObject.reject)
                                    .done(status => {
                                        switch (status) {
                                            case "success":
                                                _startTrialClicked = false;
                                                _signupSessionStore.savePaymentIsComplete(true)
                                                    .fail(deferredObject.reject)
                                                    .done(() => {
                                                        _signupSessionStore.saveSignupSessionStatus(_signupSessionStatusConstants.accountCreationInProgress)
                                                            .fail(deferredObject.reject)
                                                            .done(() => {
                                                                deferredObject.resolve({keepSpinning: false});
                                                                _signupNavigationManager.navigateToSuccessPage();
                                                            });
                                                    });
                                                break;
                                            case "credit_card_declined":
                                            case "payment_gateway_down":
                                            case "create_account_failed":
                                            case "invalid_email_address":
                                            case "invalid_postal_code":
                                            case "no_email_address_provided":
                                            case "no_mobile_phone_number_provided":
                                            case "no_mobile_phone_number_country_provided":
                                                self.signupResult(status);
                                                deferredObject.resolve({keepSpinning: false});
                                                break;
                                            case "error_no_phone_numbers":
                                            case "failed_to_reserve_phone_numbers":
                                                _signupSessionStore.get()
                                                    .fail(deferredObject.reject)
                                                    .done(signupDocument => {
                                                        self.modalPhoneNumber(_phoneNumberFormatter.toUSAreaCode(signupDocument.reservedPhoneNumber));
                                                        deferredObject.resolve({keepSpinning: false});
                                                        self.isModalVisible(true);
                                                        _reservationFailedRouteId = _phoneNumberFormatter.isTollFree(signupDocument.reservedPhoneNumber) ?
                                                            _navigationConfiguration.routesById.signupPhoneNumberTollfree.routeId :
                                                            _navigationConfiguration.routesById.signupPhoneNumberLocal.routeId;
                                                    });
                                                break;
                                            case "authorized_signature_required":
                                                deferredObject.resolve({keepSpinning: false});
                                                _showModalOther(MODAL_OTHER_AUTHORIZED_SIGNATURE);
                                                break;
                                            case "no_password_provided":
                                                deferredObject.resolve({keepSpinning: false});
                                                _showModalOther(MODAL_OTHER_PASSWORD);
                                                break;
                                            default:
                                                self.signupResult(status);
                                                deferredObject.resolve({keepSpinning: false});
                                        }
                                        _startTrialClicked = false;
                                    });
                            });
                    });
            });

            self.billingMethods = null;
            self.countries = null;
            self.statesProvinces = null;
            self.expirationMonths = null;
            self.expirationYears = null;

            self.cardHolderName = ko.observable("");
            self.cardHolderNameThrottled = ko.computed(self.cardHolderName).extend({throttle: 300});
            self.cardNumber = ko.observable("").extend({ rateLimit: 500 });
            self.addressLineOne = ko.observable('').extend({ rateLimit: 500 });
            self.addressLineTwo = ko.observable('').extend({ rateLimit: 500 });
            self.city = ko.observable('').extend({ rateLimit: 500 });
            self.stateOrProvince = ko.observable("");
            self.country = ko.observable("");
            self.cvvCode = ko.observable("");
            self.errorNotification = ko.observable();
            self.expirationDate = ko.observable();
            self.expirationMonth = ko.observable("");
            self.expirationYear = ko.observable("");
            self.freeTrialEndDate = _createFreeTrialEndDate();
            self.isSignupForCustomer = ko.observable(_isSignupForCustomer);
            self.isBillingMethodVisible = ko.observable(_isSignupForCustomer);
            self.selectedBillingMethod = ko.observable([_billingMethodConstants.CreditCard.toString()]);
            self.isCompositionComplete = ko.observable(false);
            self.showValidation = ko.observable(false);
            self.isNavigateToNextPageActive = ko.observable(true);

            self.headingText = _i18n.t('signupPaymentMethod:headingText');
            self.subtitleText = _i18n.t('signupPaymentMethod:subtitleText');
            self.nextPageButtonText = _i18n.t('signupPaymentMethod:start');

            self.isValid = ko.observable(false);
            self.postalCode = ko.observable("");
            self.signupResult = ko.observable("");

            self.isCardEntryVisible = ko.pureComputed(() => {
                return self.selectedBillingMethod()[0].toString() === _billingMethodConstants.CreditCard.toString();
            });

            self.stateOrProvinceLabelI18nKey = ko.pureComputed(() => {
                return _countries.findByAbbreviation(self.country()[0]).hasProvinces ?
                    'signupPaymentMethod:province' :
                    'signupPaymentMethod:state';
            });
            self.zipOrPostalCodeMetaData = ko.pureComputed(() => _countries.findByAbbreviation(self.country()[0]).hasProvinces ?
                {i18nKey: 'postalCode', type: 'postalcode', maxLength: 6} :
                {i18nKey: 'zipCode', type: 'zipcode', maxLength: 5}
            );

            self.selectPlaceHolderMessage = ko.pureComputed(() => _i18n.t("signupPaymentMethod:select"));

            self.openTermsOfAgreement = () => {
                const windowControl = require('presentation/common/window/windowControl');
                windowControl.openTermsOfAgreement();
            };

            self.isScrollActive = ko.observable(false);

            self.isModalVisible = ko.observable(false);
            self.isXCloseVisible = false;
            self.modalPhoneNumber = ko.observable("");
            self.isModalOtherVisible = ko.observable(false);
            self.modalOtherText = ko.observable('');
            self.modalOtherButtonText = ko.observable('');

            self.isBypassPreAuthorizationVisible = ko.observable(_isSignupForCustomer);
            self.isBypassPreAuthorizationSelected = ko.observable(false);
            self.isBypassWelcomeEmailSelected = ko.observable(false);

            self.isAddressValid = ko.observable(false);
            self.isCardFieldsValid = ko.observable(false);

            self.isValid = ko.pureComputed(() => {
                return self.isAddressValid() && (self.isCardEntryVisible() === false || self.isCardFieldsValid());
            });

            self.modalCloseClick = () => {
                const flowId = _signupNavigationManager.getFlowId();
                switch (flowId) {
                    case _signupSessionFlowConstants.flowA:
                    case _signupSessionFlowConstants.flowAgent:
                    case _signupSessionFlowConstants.flowB:
                    case _signupSessionFlowConstants.flowD:
                        _signupNavigationManager.navigateToRoute(_reservationFailedRouteId, flowId);
                        break;
                    case _signupSessionFlowConstants.flowC:
                        _signupNavigationManager.navigateToRoute(_reservationFailedRouteId, _signupSessionFlowConstants.flowB);
                        break;
                }
            };

            self.modalOtherCloseClick = () => {
                if (_modalOtherNavigationRouteId === null) {
                    return;
                }

                const flowId = _signupNavigationManager.getFlowId();
                _signupNavigationManager.navigateToRoute(_modalOtherNavigationRouteId, flowId);
            };

            self.validate = function() {
                return _promiseFactory.defer(function(deferredObject) {
                    _validator.validate()
                        .fail(deferredObject.reject)
                        .done((isValid) => {
                            self.showValidation(true);
                            deferredObject.resolve(isValid);
                        });
                });
            };

            self.detached = function() {
                _signupNavigationManager.detached();
                _disposables.forEach(subscription => subscription.dispose());
                _disposables = [];
            };

            self.compositionComplete = () => {
                self.isCompositionComplete(true);
            };

            self.activate = function() {
                const SignupSessionStoreConstructor = require('businessServices/signup/signupSessionStore');
                _signupSessionStore = new SignupSessionStoreConstructor();
                _signupSessionStore.init();

                const SignupNavigationManagerConstructor = require('presentation/signup/common/signupNavigationManager');
                _signupNavigationManager = new SignupNavigationManagerConstructor();
                _signupNavigationManager.init();

                _facade = new FacadeConstructor();
                _facade.init();

                _validator = new ValidatorConstructor();

                return _initialize();
            };


            const _initialize = () => {
                _validator.registerViewModel(self);

                _promiseFactory.deferIndefinitely(deferredObject => {
                    _populateBillingMethodDropdown();
                    _populateCountryDropdown();
                    _populateExpirationDateDropdowns();
                    _populateStateDropdown();

                    _facade.loadSignupSession()
                        .fail(deferredObject.reject)
                        .done(signupPaymentMethodPresentationObject => {
                            if (signupPaymentMethodPresentationObject.cardHolderName) {
                                self.cardHolderName(signupPaymentMethodPresentationObject.cardHolderName);
                            }
                            if (signupPaymentMethodPresentationObject.addressLineOne) {
                                self.addressLineOne(signupPaymentMethodPresentationObject.addressLineOne);
                            }
                            if (signupPaymentMethodPresentationObject.addressLineTwo) {
                                self.addressLineTwo(signupPaymentMethodPresentationObject.addressLineTwo);
                            }
                            if (signupPaymentMethodPresentationObject.city) {
                                self.city(signupPaymentMethodPresentationObject.city);
                            }
                            if (signupPaymentMethodPresentationObject.postalCode) {
                                self.postalCode(signupPaymentMethodPresentationObject.postalCode);
                            }
                            if (signupPaymentMethodPresentationObject.country) {
                                self.country([_currentCountry(signupPaymentMethodPresentationObject.country).id]);
                                self.statesProvinces(_prepareStatesProvinces(_countryRegions.findByAbbreviation(self.country()[0]).statesProvinces));
                            } else {
                                self.country([_currentCountry().id]);
                                self.statesProvinces(_prepareStatesProvinces(_countryRegions.findByAbbreviation(self.country()[0]).statesProvinces));
                            }
                            if (signupPaymentMethodPresentationObject.state) {
                                const currentState = self.statesProvinces().find((state) => {
                                    return state.id === signupPaymentMethodPresentationObject.state;
                                });
                                if (currentState) {
                                    self.stateOrProvince([currentState.id]);
                                } else {
                                    self.stateOrProvince(self.statesProvinces()[0].id);
                                }
                            } else {
                                self.stateOrProvince([self.statesProvinces()[0].id]);
                            }
                            if (signupPaymentMethodPresentationObject.expirationDate !== "0001-01-01T00:00:00Z") {
                                const date = new DateConstructor();
                                date.setFromDateString(signupPaymentMethodPresentationObject.expirationDate);
                                self.expirationMonth([_currentMonth(date.monthPadded()).id]);
                                self.expirationYear([_currentYear(date.year()).id]);
                            } else {
                                self.expirationMonth([_currentMonth().id]);
                                self.expirationYear([_currentYear().id]);
                            }
                            if (signupPaymentMethodPresentationObject.shouldBypassWelcomeEmail) {
                                self.isBypassWelcomeEmailSelected(true);
                            }
                            if (signupPaymentMethodPresentationObject.billingMethod) {
                                const currentBillingMethod = self.billingMethods.find((method) => {
                                    return method.id === signupPaymentMethodPresentationObject.billingMethod.toString();
                                });
                                if (currentBillingMethod) {
                                    self.selectedBillingMethod([currentBillingMethod.id.toString()]);
                                } else {
                                    self.selectedBillingMethod(self.billingMethods[0].id.toString());
                                }
                            }

                            _saveStateOrProvince();

                            _disposables.push(
                                self.selectedBillingMethod.subscribe(_onBillingMethodChange),
                                self.cardHolderNameThrottled.subscribePrevious(_saveCardHolderName),
                                self.expirationMonth.subscribe(_saveExpirationDate),
                                self.expirationYear.subscribe(_saveExpirationDate),
                                self.addressLineOne.subscribe(_saveAddressLineOne),
                                self.addressLineTwo.subscribe(_saveAddressLineTwo),
                                self.city.subscribe(_saveCity),
                                self.stateOrProvince.subscribe(_saveStateOrProvince),
                                self.country.subscribe(_onCountryChange),
                                self.postalCode.subscribePrevious(_savePostalCode)
                            );

                            deferredObject.resolve();
                        });
                });
                return _promiseFactory.wait();
            };
        };
    });

